import { Link } from "react-router-dom";
import { AlertTriangle, TrendingUp, TrendingDown } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { formatShortDate } from ".";
import { formatMoneyCompact } from "@/lib/format";

interface TopTrip {
  id: string;
  title: string;
  revenue: number;
  bookings: number;
  occupancy: number;
}

interface LowOccupancyItem {
  id: string;
  title: string;
  dateId: string;
  startDate: string;
  occupancy: number;
}

interface TrendingDestination {
  destination: string;
  currentCount: number;
  previousCount: number;
  changeYoY: number;
}

interface OccupancyTableProps {
  topTrips: TopTrip[];
  lowOccupancy: LowOccupancyItem[];
  trendingDestinations: TrendingDestination[];
  avgGroupSize: number;
}

export default function OccupancyTable({
  topTrips,
  lowOccupancy,
  trendingDestinations,
  avgGroupSize,
}: OccupancyTableProps) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Top Trips */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          {t("system.analytics.trips.topTrips")}
        </h3>
        <div className="space-y-3">
          {topTrips.map((trip, idx) => (
            <Link
              key={trip.id}
              to={`/trips/${trip.id}`}
              className="flex items-center justify-between rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
            >
              <div className="flex items-center gap-3">
                <span className="bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold">
                  {idx + 1}
                </span>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{trip.title}</p>
                  <p className="text-sm text-gray-500">
                    {t("system.analytics.trips.bookings", { count: trip.bookings })}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-900 dark:text-white">
                  {formatMoneyCompact(trip.revenue)}
                </p>
                <div className="flex items-center justify-end gap-1">
                  <div className={clsx("h-1.5 w-16 rounded-full bg-gray-200 dark:bg-gray-700")}>
                    <div
                      className={clsx(
                        "h-full rounded-full",
                        trip.occupancy >= 80
                          ? "bg-green-500"
                          : trip.occupancy >= 50
                            ? "bg-yellow-500"
                            : "bg-red-500",
                      )}
                      style={{ width: `${Math.min(trip.occupancy, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500">{trip.occupancy}%</span>
                </div>
              </div>
            </Link>
          ))}
          {topTrips.length === 0 && (
            <p className="text-center text-gray-500">{t("system.analytics.trips.noTrips")}</p>
          )}
        </div>
      </div>

      {/* Low Occupancy Warning */}
      <div className="card">
        <div className="mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("system.analytics.trips.lowOccupancy")}
          </h3>
        </div>
        <div className="space-y-2">
          {lowOccupancy.map((item) => (
            <Link
              key={item.dateId}
              to={`/trips/${item.id}`}
              className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-3 transition-colors hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-900/20 dark:hover:bg-amber-900/30"
            >
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{item.title}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {formatShortDate(item.startDate)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded bg-amber-200 px-2 py-0.5 text-sm font-medium text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                  {item.occupancy}%
                </span>
              </div>
            </Link>
          ))}
          {lowOccupancy.length === 0 && (
            <p className="text-center text-green-600">
              {t("system.analytics.trips.allGoodOccupancy")}
            </p>
          )}
        </div>
      </div>

      {/* Trending Destinations */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          {t("system.analytics.trips.trendingDestinations")}
        </h3>
        <div className="space-y-2">
          {trendingDestinations.slice(0, 8).map((dest) => (
            <div
              key={dest.destination}
              className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-800"
            >
              <span className="font-medium text-gray-900 dark:text-white">{dest.destination}</span>
              <div className="flex items-center gap-2">
                {dest.changeYoY > 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                ) : dest.changeYoY < 0 ? (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                ) : null}
                <span
                  className={clsx(
                    "text-sm font-medium",
                    dest.changeYoY > 0
                      ? "text-green-600"
                      : dest.changeYoY < 0
                        ? "text-red-600"
                        : "text-gray-500",
                  )}
                >
                  {dest.changeYoY > 0 ? "+" : ""}
                  {dest.changeYoY}%
                </span>
              </div>
            </div>
          ))}
          {trendingDestinations.length === 0 && (
            <p className="text-center text-gray-500">{t("system.analytics.trips.notEnoughData")}</p>
          )}
        </div>
      </div>

      {/* Average Group Size */}
      <div className="card flex flex-col items-center justify-center">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
          {t("system.analytics.trips.avgGroupSize")}
        </p>
        <p className="text-primary-600 dark:text-primary-400 mt-2 text-5xl font-bold">
          {avgGroupSize}
        </p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t("system.analytics.trips.participantsPerOrder")}
        </p>
      </div>
    </div>
  );
}
